import { buyRequestsNavPath } from '@/lib/buyRequestTabs';
import { markDirectNav } from '@/lib/missionReturn';
import { BelowHeaderSlot } from '@/components/layout/BelowHeaderSlot';
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useUnreadMessages } from '@/hooks/useUnreadMessages';
import { usePendingInvitations } from '@/hooks/usePendingInvitations';
import { useHeaderData } from '@/hooks/useHeaderData';
import { useSystemAdmin } from '@/hooks/useSystemAdmin';
import { useUserPersona } from '@/hooks/useUserPersona';
import { useDeskHabit } from '@/hooks/useDeskHabit';
import { deskTitle } from '@/lib/yourDesk';
import { useEntitlements } from '@/hooks/useEntitlements';
import { usePlanState } from '@/hooks/usePlanState';
import { useReferralAttributionClaim } from '@/hooks/useReferralAttributionClaim';
import { useBulkImport } from '@/contexts/BulkImportContext';
import { Button } from '@/components/ui/button';
import { useNavAttention } from '@/hooks/useNavAttention';
import { openFeedback } from '@/components/FeedbackButton';
import { MessageSquarePlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { MobileTabBar } from '@/components/layout/MobileTabBar';
import { mobileTabsFor } from '@/lib/mobileTabs';
import { prefetchRoute } from '@/lib/routePrefetch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { LogOut, LogIn, Building2, Menu, X, User, ChevronDown, BarChart3, MessageSquare, Package, ClipboardList, Store, Shield, CreditCard, Lock, Wallet, LifeBuoy, FileText, ShieldCheck, Eye } from 'lucide-react';
import AskCannafaxMark from '@/components/ask-cannafax/AskCannafaxMark';
import NotificationBell from '@/components/notifications/NotificationBell';
import { useAskCannafax } from '@/contexts/AskCannafaxContext';
import { AskCannafaxGlow } from '@/components/ask-cannafax/AskCannafaxGlow';
import { useIsMobile } from '@/hooks/use-mobile';
import cannafaxLogo from '@/assets/cannafax-logo-clean.png';
import { getOptimizedLogoUrl, getOptimizedProfileUrl } from '@/lib/imageUtils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type ActiveRoute = 'my-listings' | 'desk' | 'messages' | 'my-menu' | 'supplier-menus' | 'buy-requests' | 'watchlist' | 'none';
interface AppHeaderProps {
  activeRoute?: ActiveRoute;
}
/* Following (/watchlist) is a child of Buy Requests (see src/lib/pageLevels.ts), so it lights Buy Requests. */
const ROUTE_PREFIXES: Array<[string, ActiveRoute]> = [
  ['/my-listings', 'my-listings'], ['/listings', 'my-listings'], 
  ['/desk', 'desk'], ['/messages', 'messages'], ['/my-menu', 'my-menu'],
  ['/supplier-menus', 'supplier-menus'], ['/buy-requests', 'buy-requests'], ['/watchlist', 'buy-requests'],
];
const AppHeader = ({
  activeRoute: activeRouteProp = 'none'
}: AppHeaderProps) => {
  // Highlight follows the URL, so a page can never forget to light up its own nav link.
  const { pathname } = useLocation();
  const activeRoute: ActiveRoute = activeRouteProp !== 'none'
    ? activeRouteProp
    : ROUTE_PREFIXES.find(([p]) => pathname === p || pathname.startsWith(`${p}/`))?.[1] ?? 'none';
  const {
    user,
    signOut
  } = useAuth();
  // Credit a referral link to this company once, as soon as they are signed in.
  useReferralAttributionClaim();
  const {
    unreadCount
  } = useUnreadMessages();
  const {
    pendingCount: pendingInvitations
  } = usePendingInvitations();
  const { incomingAccessRequests, newRequestMatches } = useNavAttention();
  const {
    data: headerData,
    isLoading: headerLoading
  } = useHeaderData();
  const {
    isSystemAdmin
  } = useSystemAdmin();
  const {
    getLandingPage
  } = useUserPersona();
  // Your Desk is named for the stage the company is in: Desk Setup, then Your Next Move™.
  const { progress: deskProgress, reps: deskReps } = useDeskHabit();
  const deskNavLabel = deskTitle(!!deskProgress?.graduated);
  // One "start here" at a time: the Ask Cannafax welcome waits until the first Desk Setup step is done.
  const holdAskGlow = !deskProgress || (!deskProgress.graduated && !deskReps.some((r) => r.done));
  /* The header shows the company's PLAN — the only subscription identity users have.
     There is no "company role" surface anywhere in the product (C-RL-02). */
  const {
    plan
  } = usePlanState();
  const {
    canOrUnknown,
    isLoading: subscriptionLoading
  } = useEntitlements();
  /* Nav visibility is a capability question, never a plan-name question. While the
     answer is still resolving we show the item — hiding a seller's daily route on a
     slow request would break the habit rung, and the server guard is the real wall. */
  const canInventory = canOrUnknown('inventory.manage');
  const canMenus = canOrUnknown('menus.manage');
  // Your Desk is every persona's daily home (buyers included) — never plan-gated.
  const canInsights = true;
  const {
    showBanner
  } = useBulkImport();
  const {
    toggle: toggleAskCannafax
  } = useAskCannafax();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const isHeaderHydrating = !!user && (headerLoading || subscriptionLoading);
  // Desktop and phone layouts are separate blocks (only one shows), so the same mark renders in each.
  const askMark = <AskCannafaxGlow hold={holdAskGlow}><Button variant="quiet" size="content" type="button" onClick={toggleAskCannafax} aria-label="Ask Cannafax" title="Ask Cannafax (⌘K)" className="p-0 [&_svg]:size-auto shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"><AskCannafaxMark size="md" /></Button></AskCannafaxGlow>;
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 10;

      // Always show header at top of page
      if (currentScrollY < scrollThreshold) {
        setIsVisible(true);
        lastScrollY.current = currentScrollY;
        return;
      }

      // Determine scroll direction
      if (currentScrollY > lastScrollY.current + scrollThreshold) {
        // Scrolling down - hide header (only if mobile menu is closed)
        if (!mobileMenuOpen) {
          setIsVisible(false);
        }
      } else if (currentScrollY < lastScrollY.current - scrollThreshold) {
        // Scrolling up - show header
        setIsVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [mobileMenuOpen]);
  const prefetchAdmin = () => {
    prefetchRoute('/admin');
    if (user) {
      queryClient.prefetchQuery({
        queryKey: ['systemAdmin', user.id],
        queryFn: async () => {
          const {
            data
          } = await supabase.from('system_admins').select('id').eq('user_id', user.id).maybeSingle();
          return !!data;
        },
        staleTime: 5 * 60 * 1000
      });
    }
  };
  const companyLogo = headerData?.companyLogo ?? null;
  const companyName = headerData?.companyName ?? null;
  const profilePicture = headerData?.profilePicture ?? null;
  const isAdmin = headerData?.isAdmin ?? false;
  const handleSignOut = async () => {
    setMobileMenuOpen(false);
    setSigningOut(true);
    try {
      await signOut();
    } catch {
      // signOut handles errors internally
    }
    sessionStorage.setItem('signing_out', '1');
    window.location.href = '/';
  };
  const getLinkClass = (route: ActiveRoute, isDisabled?: boolean) => {
    if (isDisabled) {
      return 'text-sm text-muted-foreground/50 cursor-not-allowed';
    }
    return route === activeRoute ? 'text-sm text-foreground font-medium' : 'text-sm text-muted-foreground hover:text-foreground transition-colors';
  };
  const getMobileLinkClass = (route: ActiveRoute, isDisabled?: boolean) => {
    if (isDisabled) {
      return 'block px-4 py-3 text-muted-foreground/50 cursor-not-allowed';
    }
    return route === activeRoute ? 'block px-4 py-3 text-foreground font-medium bg-muted/50' : 'block px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors';
  };
  const handleRestrictedClick = (e: React.MouseEvent, featureName: string) => {
    e.preventDefault();
    e.stopPropagation();
    toast.warning('Seller subscription required', {
      description: `${featureName} is available with a Seller subscription.`,
      action: {
        label: 'View Plans',
        onClick: () => navigate('/pricing'),
      },
    });
  };
  const menuBody = <>
          {/* Company branding for mobile */}
          {user && (companyLogo || companyName) && <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-muted/30">
              {companyLogo ? <img src={getOptimizedLogoUrl(companyLogo, 'md') || companyLogo} alt="Company logo" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 rounded bg-subtle border border-border flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                </div>}
              <div className="flex flex-col gap-1">
                {companyName && <span className="text-sm font-medium">{companyName}</span>}
                {user.email && <span className="text-xs text-muted-foreground">{user.email}</span>}
                {plan && <Link to="/company-settings#plan" onClick={() => setMobileMenuOpen(false)} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 w-fit mt-1 text-xs font-medium text-foreground/80 transition-colors hover:bg-primary/10 hover:border-primary/40 hover:text-primary">
                    <CreditCard className="h-3 w-3 text-muted-foreground" />
                    <span>{plan.name} plan</span>
                    <ChevronDown className="h-3 w-3 -rotate-90 text-muted-foreground" />
                  </Link>}
              </div>
            </div>}

          {/* Mobile Navigation Links */}
          <nav onClickCapture={markDirectNav} className="py-2">
            {user && <>
                {canInsights ? <Link to="/desk" className={getMobileLinkClass('desk')} onClick={() => setMobileMenuOpen(false)}>
                    <span className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      {deskNavLabel}
                    </span>
                  </Link> : <Button variant="quiet" size="content" type="button" onClick={e => {
              handleRestrictedClick(e, deskNavLabel);
              setMobileMenuOpen(false);
            }} className={cn("justify-start font-normal", `${getMobileLinkClass('desk', true)} w-full text-left`)}>

                    <span className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      <Lock className="w-3 h-3" />
                      {deskNavLabel}
                    </span>
                  </Button>}
                {canInventory ? <Link to="/my-listings" className={getMobileLinkClass('my-listings')} onClick={() => setMobileMenuOpen(false)}>
                    <span className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      My Inventory
                    </span>
                  </Link> : <Button variant="quiet" size="content" type="button" onClick={e => {
              handleRestrictedClick(e, 'My Inventory');
              setMobileMenuOpen(false);
            }} className={cn("justify-start font-normal", `${getMobileLinkClass('my-listings', true)} w-full text-left`)}>

                    <span className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      <Lock className="w-3 h-3" />
                      My Inventory
                    </span>
                  </Button>}
                {canMenus ? <Link to="/my-menu" className={getMobileLinkClass('my-menu')} onClick={() => setMobileMenuOpen(false)}>
                    <span className="flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      My Menus
                      {incomingAccessRequests > 0 && <span className="circle-2.5 bg-destructive animate-pulse" />}
                    </span>
                  </Link> : <Button variant="quiet" size="content" type="button" onClick={e => {
              handleRestrictedClick(e, 'My Menus');
              setMobileMenuOpen(false);
            }} className={cn("justify-start font-normal", `${getMobileLinkClass('my-menu', true)} w-full text-left`)}>

                    <span className="flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      <Lock className="w-3 h-3" />
                      My Menus
                    </span>
                  </Button>}
                <div className="my-1 mx-4 h-px bg-border" />
                <Link to="/supplier-menus" className={getMobileLinkClass('supplier-menus')} onClick={() => setMobileMenuOpen(false)}>
                  <span className="flex items-center gap-2">
                    <Store className="w-4 h-4" />
                    Supplier Menus
                    {pendingInvitations > 0 && <span className="circle-2.5 bg-destructive animate-pulse" />}
                  </span>
                </Link>
                <Link to="/watchlist" className={getMobileLinkClass('watchlist')} onClick={() => setMobileMenuOpen(false)}>
                  <span className="flex items-center gap-2 pl-6 text-sm">
                    <Eye className="w-4 h-4" />
                    Following
                  </span>
                </Link>
                <div className="my-1 mx-4 h-px bg-border" />
                <Link to={buyRequestsNavPath(newRequestMatches)} className={getMobileLinkClass('buy-requests')} onClick={() => setMobileMenuOpen(false)}>
                  <span className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Buy Requests
                    {newRequestMatches > 0 && <span className="circle-2.5 bg-destructive animate-pulse" />}
                  </span>
                </Link>
                <Link to="/messages" className={getMobileLinkClass('messages')} onClick={() => setMobileMenuOpen(false)}>
                  <span className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Messages
                    {unreadCount > 0 && <span className="circle-2.5 bg-destructive animate-pulse" />}
                  </span>
                </Link>
                <div className="my-1 mx-4 h-px bg-border" />
                <Link to="/profile-settings" className={getMobileLinkClass('none')} onClick={() => setMobileMenuOpen(false)}>
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Profile Settings
                  </span>
                </Link>
                {isAdmin && <Link to="/company-settings" className={getMobileLinkClass('none')} onClick={() => setMobileMenuOpen(false)}>
                    <span className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Company Settings
                    </span>
                  </Link>}
                {isAdmin && <Link to="/company-settings#revshare" className={getMobileLinkClass('none')} onClick={() => setMobileMenuOpen(false)}>
                  <span className="flex items-center gap-2">
                    <Wallet className="w-4 h-4" />
                    Partner RevShare
                  </span>
                </Link>}
                <Button variant="quiet" size="content" type="button" className={cn("justify-start font-normal", cn(getMobileLinkClass('none'), 'w-full text-left'))} onClick={() => { setMobileMenuOpen(false); openFeedback(); }}>
                  <span className="flex items-center gap-2">
                    <MessageSquarePlus className="w-4 h-4" />
                    Send feedback
                  </span>
                </Button>

                {isSystemAdmin && <>
                  <div className="my-1 mx-4 h-px bg-border" />
                  <Link to="/admin" className={getMobileLinkClass('none')} onClick={() => setMobileMenuOpen(false)} onMouseEnter={prefetchAdmin}>
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Admin Console
                    </span>
                  </Link>
                </>}
              </>}
          </nav>

          {/* Mobile Auth Actions */}
          <div className="border-t border-border py-2">
            {user ? <Button variant="quiet" size="content" type="button" disabled={signingOut} className="justify-start font-normal w-full text-left px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors flex items-center gap-2 disabled:opacity-50" onClick={handleSignOut}>
                <LogOut className="w-4 h-4" />
                {signingOut ? 'Signing out…' : 'Sign Out'}
              </Button> : <Link to="/" className="block px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <span className="flex items-center gap-2">
                  <LogIn className="w-4 h-4" />
                  Sign In
                </span>
              </Link>}
          </div>
  </>;
  return <>
    <header className={`border-b border-border bg-background fixed left-0 right-0 z-50 ${isHeaderHydrating ? 'transition-none' : 'transition-all duration-300'} ${showBanner ? 'top-[52px]' : 'top-0'} ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}>
      <div className="container-wide py-4 flex items-center justify-between">
        {/* Left side: the Cannafax mark alone — plan status lives in the account menu */}
        <Link to={user ? getLandingPage() : '/'} className="flex items-center gap-2 flex-shrink-0">
          <img alt="cannafax.ai" className="h-8 w-auto object-contain" src={cannafaxLogo} />
        </Link>

        {/* Right side: Nav + identity lockup */}
        <div className="hidden min-[1200px]:flex items-center gap-3 xl:gap-4 min-w-0 flex-nowrap ml-4 xl:ml-6">
          {/* Desktop Navigation: Workspace | Catalog | Deals */}
          <nav onClickCapture={markDirectNav} className="flex items-center gap-3 xl:gap-6 flex-shrink-0 flex-nowrap whitespace-nowrap">
            {user && <>
                {canInsights ? <Link to="/desk" className={getLinkClass('desk')} onMouseEnter={() => prefetchRoute('/desk')}>
                    {deskNavLabel}
                  </Link> : <Button variant="quiet" size="content" type="button" onClick={e => handleRestrictedClick(e, deskNavLabel)} className={cn("justify-start font-normal", `${getLinkClass('desk', true)} flex items-center gap-1 whitespace-nowrap`)}>

                    <Lock className="w-3 h-3" />
                    {deskNavLabel}
                  </Button>}
                {canInventory ? <Link to="/my-listings" className={getLinkClass('my-listings')} onMouseEnter={() => prefetchRoute('/my-listings')}>
                    My Inventory
                  </Link> : <Button variant="quiet" size="content" type="button" onClick={e => handleRestrictedClick(e, 'My Inventory')} className={cn("justify-start font-normal", `${getLinkClass('my-listings', true)} flex items-center gap-1 whitespace-nowrap`)}>

                    <Lock className="w-3 h-3" />
                    My Inventory
                  </Button>}
                {canMenus ? <Link to={incomingAccessRequests > 0 ? '/my-menu?tab=access&sub=requests' : '/my-menu'} className={`${getLinkClass('my-menu')} relative whitespace-nowrap`} onMouseEnter={() => prefetchRoute('/my-menu')} aria-label={incomingAccessRequests > 0 ? `My Menus, ${incomingAccessRequests} access ${incomingAccessRequests === 1 ? 'request' : 'requests'} waiting` : undefined}>
                    My Menus
                    {incomingAccessRequests > 0 && <span className="absolute -top-1 -right-1 circle-2.5 bg-destructive animate-pulse" />}
                  </Link> : <Button variant="quiet" size="content" type="button" onClick={e => handleRestrictedClick(e, 'My Menus')} className={cn("justify-start font-normal", `${getLinkClass('my-menu', true)} flex items-center gap-1 whitespace-nowrap`)}>

                    <Lock className="w-3 h-3" />
                    My Menus
                  </Button>}
                <div className="h-4 w-px bg-border" />
                <Link to="/supplier-menus" className={`${getLinkClass('supplier-menus')} relative whitespace-nowrap`} onMouseEnter={() => prefetchRoute('/supplier-menus')}>
                  Supplier Menus
                  {pendingInvitations > 0 && <span className="absolute -top-1 -right-1 circle-2.5 bg-destructive animate-pulse" />}
                </Link>
                <div className="h-4 w-px bg-border" />
                <Link to={buyRequestsNavPath(newRequestMatches)} className={`${getLinkClass('buy-requests')} relative whitespace-nowrap`} onMouseEnter={() => prefetchRoute('/buy-requests')} aria-label={newRequestMatches > 0 ? `Buy Requests, ${newRequestMatches} new ${newRequestMatches === 1 ? 'match' : 'matches'}` : undefined}>
                  Buy Requests
                  {newRequestMatches > 0 && <span className="absolute -top-1 -right-1 circle-2.5 bg-destructive animate-pulse" />}
                </Link>
                <Link to="/messages" className={`${getLinkClass('messages')} relative whitespace-nowrap`} onMouseEnter={() => prefetchRoute('/messages')}>
                  Messages
                  {unreadCount > 0 && <span className="absolute -top-1 -right-1 circle-2.5 bg-destructive animate-pulse" />}
                </Link>
              </>}
          </nav>

          {user ? <>
              {/* The assistant's one permanent home: same spot on every page and screen size. */}
              {askMark}
              {/* In-app inbox: timely rewards without waiting for email */}
              <NotificationBell />

              {/* Identity lockup: company logo + user avatar; names live inside the menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1.5 pl-1.5 shrink-0" aria-label={companyName ? `${companyName} account menu` : 'Account menu'} title={companyName || undefined}>
                    {(companyLogo || companyName) && (companyLogo
                      ? <img src={getOptimizedLogoUrl(companyLogo, 'sm') || companyLogo} alt="" className="w-8 h-8 shrink-0 rounded object-cover" />
                      : <div className="w-8 h-8 shrink-0 bg-subtle border border-border flex items-center justify-center">
                          <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>)}
                    <div className="circle-8 bg-subtle border border-border flex items-center justify-center overflow-hidden">
                      {profilePicture ? <img src={getOptimizedProfileUrl(profilePicture, 'sm') || profilePicture} alt="Profile" className="w-full h-full object-cover" /> : <User className="w-3.5 h-3.5 text-muted-foreground" />}
                    </div>
                    <ChevronDown className="w-3 h-3 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 space-y-0.5">
                    {companyName && <p className="text-sm font-medium truncate">{companyName}</p>}
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                    {plan && <Link to="/company-settings#plan" className="group mt-1 inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs font-medium text-foreground/80 transition-colors hover:bg-primary/10 hover:border-primary/40 hover:text-primary">
                        <CreditCard className="h-3 w-3 text-muted-foreground group-hover:text-primary transition-colors" />
                        <span>{plan.name} plan</span>
                        <ChevronDown className="h-3 w-3 -rotate-90 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </Link>}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile-settings" className="cursor-pointer">
                      <User className="w-4 h-4 mr-2" />
                      Profile Settings
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && <DropdownMenuItem asChild>
                      <Link to="/company-settings" className="cursor-pointer">
                        <Building2 className="w-4 h-4 mr-2" />
                        Company Settings
                      </Link>
                    </DropdownMenuItem>}
                  {isAdmin && <DropdownMenuItem asChild>
                    <Link to="/company-settings#revshare" className="cursor-pointer">
                      <Wallet className="w-4 h-4 mr-2" />
                      Partner RevShare
                    </Link>
                  </DropdownMenuItem>}

                  {isSystemAdmin && <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild onMouseEnter={prefetchAdmin}>
                      <Link to="/admin" className="cursor-pointer">
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Console
                      </Link>
                    </DropdownMenuItem>
                  </>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/support" className="cursor-pointer">
                      <LifeBuoy className="w-4 h-4 mr-2" />
                      Contact support
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={openFeedback} className="cursor-pointer">
                    <MessageSquarePlus className="w-4 h-4 mr-2" />
                    Send feedback
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/customer-agreement" className="cursor-pointer">
                      <FileText className="w-4 h-4 mr-2" />
                      Customer Agreement
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/terms" className="cursor-pointer">
                      <FileText className="w-4 h-4 mr-2" />
                      Terms of Service
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/privacy" className="cursor-pointer">
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Privacy Policy
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} disabled={signingOut} className="text-destructive focus:text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    {signingOut ? 'Signing out…' : 'Sign Out'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </> : <Link to="/">
              <Button variant="ghost" size="sm">
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </Button>
            </Link>}
        </div>

        {/* Mobile & Tablet: Ask Cannafax + Hamburger */}
        <div className="min-[1200px]:hidden flex items-center gap-1">
          {user && askMark}
          <Button variant="ghost" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} className="hidden md:inline-flex h-12 w-12 p-0 [&_svg]:!size-[28px]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </div>

      {/* Mobile & Tablet Menu */}
      {mobileMenuOpen && !isMobile && <div className="min-[1200px]:hidden absolute top-full left-0 right-0 bg-background border-b border-border shadow-lg z-50">
        {menuBody}
        </div>}
    </header>
    {/* Spacer for fixed header */}
    <div className="h-[73px]" />
    <BelowHeaderSlot />
    {user && isMobile && <MobileTabBar activeRoute={activeRoute} tabs={mobileTabsFor({ canInventory, canMenus })} badges={{ 'my-menu': incomingAccessRequests > 0, 'supplier-menus': pendingInvitations > 0, 'buy-requests': newRequestMatches > 0, messages: unreadCount > 0 }} deskLabel={deskNavLabel} buyRequestsTo={buyRequestsNavPath(newRequestMatches)} moreOpen={mobileMenuOpen} onMore={() => setMobileMenuOpen(true)} />}
    {user && isMobile && <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-2xl p-0 pb-[env(safe-area-inset-bottom)]">
        <SheetTitle className="sr-only">More</SheetTitle>
        {menuBody}
      </SheetContent>
    </Sheet>}

  </>;
};
export default AppHeader;